import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onNetworkChange, getNetworkStatus, checkApiHealth } from '../utils/apiClient';
import { toast } from 'sonner';

const NetworkContext = createContext(null);

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    return { isOnline: true, isApiHealthy: true }; // Fallback
  }
  return context;
};

export const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(getNetworkStatus());
  const [isApiHealthy, setIsApiHealthy] = useState(true);
  const [connectionQuality, setConnectionQuality] = useState('good'); // good, degraded, poor
  const lastHealthyRef = useRef(Date.now());
  const consecutiveFailuresRef = useRef(0);

  // Check API health with quality tracking
  const checkHealth = useCallback(async () => {
    if (!isOnline) {
      setIsApiHealthy(false);
      setConnectionQuality('poor');
      return false;
    }
    
    const startTime = Date.now();
    const healthy = await checkApiHealth();
    const responseTime = Date.now() - startTime;
    
    if (healthy) {
      consecutiveFailuresRef.current = 0;
      lastHealthyRef.current = Date.now();
      setIsApiHealthy(true);
      
      // Determine connection quality based on response time
      if (responseTime < 1000) {
        setConnectionQuality('good');
      } else if (responseTime < 3000) {
        setConnectionQuality('degraded');
      } else {
        setConnectionQuality('poor');
      }
    } else {
      consecutiveFailuresRef.current += 1;
      
      // Only mark as unhealthy after multiple consecutive failures
      if (consecutiveFailuresRef.current >= 2) {
        setIsApiHealthy(false);
        setConnectionQuality('poor');
      }
    }
    
    return healthy;
  }, [isOnline]);

  // Subscribe to network changes
  useEffect(() => {
    const unsubscribe = onNetworkChange((online) => {
      setIsOnline(online);
      
      if (online) {
        toast.success('Connection restored', { 
          duration: 3000,
          closeButton: true 
        });
        // Check API health when coming back online
        consecutiveFailuresRef.current = 0;
        checkHealth();
      } else {
        toast.error('Connection lost. Retrying automatically...', {
          duration: 5000,
          closeButton: true
        });
        setConnectionQuality('poor');
      }
    });

    return unsubscribe;
  }, [checkHealth]);

  // Initial health check and periodic checks
  useEffect(() => {
    checkHealth();
    
    // Check health every 45 seconds (less aggressive than before)
    const interval = setInterval(checkHealth, 45000);
    
    return () => clearInterval(interval);
  }, [checkHealth]);

  // Retry function for failed operations with backoff
  const retryOperation = useCallback(async (operation, maxRetries = 3) => {
    let lastError;
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        if (i < maxRetries - 1) {
          // Wait before retry with exponential backoff
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, i)));
        }
      }
    }
    throw lastError;
  }, []);

  const value = {
    isOnline,
    isApiHealthy,
    connectionQuality,
    checkHealth,
    retryOperation,
    lastHealthy: lastHealthyRef.current
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

export default NetworkProvider;
